import React from 'react'
import classnames from 'classnames'
import { TextVariant } from '@lounge-fe/ui-kit/src/components/Text/types'
import { Image, Notification, Text } from '@lounge-fe/ui-kit'
import ProductQuickAddBlock from '../ProductQuickAddBlock'
import {
  BlockSettings,
  ProductQuickAddSettings,
  TextBlockProps,
  HeadingProps,
  ImageBlockProps,
  NotificationBlockProps,
} from './types'
import { CollectionProducts } from '../ModalGroup/CollectionProducts'
import { Button } from '../ModalGroup/Button'
import { ImageCarouselBlock } from '../ImageCarouselBlock'
import BlockRenderer from './BlockRenderer'

export const defaultBlockRegistry: {
  [K in keyof BlockSettings]: React.ComponentType<BlockSettings[K]>
} = {
  column: ({ blocks, class_names }) => (
    <div className={class_names}>
      <BlockRenderer<BlockSettings> blocks={blocks} />
    </div>
  ),
  grid_columns: ({ blocks, vertical_alignment }) => {
    const gridColMap: { [key: number]: string } = {
      1: 'md:grid-cols-1',
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-3',
    }

    if (blocks.length > 3) {
      console.warn('grid_columns block can only support up to 3 columns')
    }

    return (
      <div
        className={classnames(
          'grid grid-cols-1 gap-y-6 sm:gap-x-8',
          gridColMap[blocks.length],
          vertical_alignment,
        )}
      >
        <BlockRenderer<BlockSettings> blocks={blocks} />
      </div>
    )
  },
  collection_products: CollectionProducts,
  button: Button,
  spacer: ({ spacer }: { spacer: string }) => <div className={spacer} />,
  text: ({
    text,
    tag = 'p',
    text_size: variant,
    text_colour,
    text_alignment,
    margin_bottom: marginBottom = 'mb-0',
  }: TextBlockProps) => (
    <Text
      as={tag}
      variant={variant as TextVariant}
      dangerouslySetInnerHTML={{ __html: text }}
      className={classnames(marginBottom, text_alignment, text_colour)}
    />
  ),
  heading: ({
    heading,
    heading_size: variant,
    is_h1,
    html_tag,
    heading_alignment,
    font_style,
  }: HeadingProps) => {
    const as = !html_tag ? (is_h1 ? 'h1' : 'h2') : html_tag

    return (
      <Text
        as={as}
        variant={variant as TextVariant}
        dangerouslySetInnerHTML={{ __html: heading }}
        className={classnames(heading_alignment, font_style)}
      />
    )
  },
  html: ({ html }: { html: string }) => (
    <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: html }} />
  ),
  product_quick_add: (props: ProductQuickAddSettings) => (
    <ProductQuickAddBlock data={props} />
  ),
  image: ({
    image,
    width,
    height,
    alignment,
    class_names,
  }: ImageBlockProps) => (
    <div className={classnames('flex', alignment, class_names)}>
      <Image
        data={{
          src: image,
          width,
          height,
          crop: 'center',
        }}
        width={width}
        height={height}
        loading="lazy"
      />
    </div>
  ),
  image_carousel: ImageCarouselBlock,
  notification: ({ notification_text }: NotificationBlockProps) => (
    <Notification severity="default">
      <span dangerouslySetInnerHTML={{ __html: notification_text }} />
    </Notification>
  ),
  link: ({ link_label, link_url, link_label_two, link_url_two, link_size }) => (
    <div className={classnames('button-group', link_size)}>
      {link_label && link_url ? (
        <a href={link_url} className={classnames('link', link_size)}>
          {link_label}
        </a>
      ) : null}
      {link_label_two && link_url_two ? (
        <a href={link_url_two} className={classnames('link', link_size)}>
          {link_label_two}
        </a>
      ) : null}
    </div>
  ),
}

export type DefaultBlockRegistry = typeof defaultBlockRegistry
